import React, { useState, useRef } from "react";
import { border, navy, red, surface, muted, danger, card, dark, tan, NATIONALITY_CODES, CATEGORIES } from "./constants";
import { Btn, Input, Textarea } from "./ui";
import { resizeToBlob, uploadToStorage, FuelGauge } from "./member";

export function FlagImg({ nationality, size = 18 }) {
  const code = NATIONALITY_CODES[nationality];
  if (!code) return <span style={{ fontSize: size * 0.9 }}>🌍</span>;
  // Convert country code to emoji flag (regional indicator symbols)
  const emoji = code.toUpperCase().split("").map(c =>
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
  ).join("");
  return <span style={{ fontSize: size * 1.1, lineHeight: 1, verticalAlign: "middle", flexShrink: 0 }}>{emoji}</span>;
}
export const CAT_BG = { Announcement: "#c8222222", Event: "#1a254022" };
export const CAT_COLOR = { Announcement: red, Event: navy };

export function fmt(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

export function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
export function fmtTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}
export function fmtDatetime(ts) {
  if (!ts) return "";
  return fmtDate(ts) + " " + fmtTime(ts);
}

export function renderBody(text) {
  if (!text) return null;
  const urlRe = /(https?:\/\/[^\s]+)/g;
  const imgRe = /^!\[\]\((https?:\/\/[^\s)]+)\)$/;
  return text.split("\n").map((line, li) => {
    // Inline image
    const imgMatch = line.match(imgRe);
    if (imgMatch) {
      return <div key={li} style={{ margin: "8px 0" }}>
        <img src={imgMatch[1]} alt="" style={{ maxWidth: "100%", borderRadius: 6,
          display: "block", maxHeight: 400, objectFit: "cover" }} />
      </div>;
    }
    const isBullet = line.startsWith("• ") || line.startsWith("- ");
    const content = isBullet ? line.slice(2) : line;
    // Parse **bold** within a line
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((seg, si) => {
      if (seg.startsWith("**") && seg.endsWith("**")) {
        return <strong key={si}>{seg.slice(2, -2)}</strong>;
      }
      // linkify plain text
      const urlParts = seg.split(urlRe);
      return urlParts.map((p, ui) =>
        urlRe.test(p)
          ? <a key={ui} href={p} target="_blank" rel="noopener noreferrer"
              style={{ color: red, textDecoration: "underline", wordBreak: "break-all" }}>{p}</a>
          : p
      );
    });
    if (isBullet) {
      return <div key={li} style={{ display: "flex", gap: 6, marginBottom: 2 }}>
        <span style={{ color: red, flexShrink: 0, fontWeight: 700 }}>•</span>
        <span>{content ? parts : ""}</span>
      </div>;
    }
    return <div key={li} style={{ minHeight: line ? undefined : "0.8em" }}>{parts}</div>;
  });
}

export function linkify(text) {
  return renderBody(text);
}

export function EventBadge({ startAt, endAt }) {
  if (!startAt) return null;
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : null;
  return (
    <div style={{ marginBottom: 10, padding: "8px 10px", background: `${navy}0e`, borderRadius: 4,
      border: `1px solid ${navy}20` }}>
      <div style={{ fontSize: 16, color: navy, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.08em" }}>
        {fmtDate(startAt)}
      </div>
      <div style={{ fontSize: 12, color: muted, fontFamily: "'Bebas Neue', sans-serif", marginTop: 2, letterSpacing: "0.06em" }}>
        {fmtTime(startAt)}{end ? ` — ${fmtTime(endAt)}` : ""}
      </div>
    </div>
  );
}

export function BoardView({ posts, isAdmin, onNewPost, onEditPost, onDeletePost, onReply, onDeleteReply }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div>
      {isAdmin && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn onClick={onNewPost}>+ New Post</Btn>
        </div>
      )}
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
      {posts.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36,
            color: `${navy}20`, letterSpacing: "0.2em", marginBottom: 10 }}>NO POSTS YET</div>
          <div style={{ fontSize: 12, color: muted }}>Check back soon for announcements and events.</div>
        </div>
      )}
      {posts.map(post => {
        const isOpen = openId === post.id;
        const replyCount = post.replies?.length || 0;
        const catColor = CAT_COLOR[post.category] || navy;
        const catBg = CAT_BG[post.category] || "#ffffff20";
        return (
          <div key={post.id} style={{ background: surface, border: `1px solid ${border}`,
            borderRadius: 6, marginBottom: 12, overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            {/* ── List row (always visible) ── */}
            <div style={{ padding: "12px 16px", cursor: "pointer", display: "flex", gap: 12, alignItems: "center" }}
              onClick={() => setOpenId(isOpen ? null : post.id)}>
              {/* Center: title + meta */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                    padding: "3px 8px", borderRadius: 3, background: catBg, color: catColor,
                    fontFamily: "'Bebas Neue', sans-serif", border: `1px solid ${catColor}30`, whiteSpace: "nowrap" }}>
                    {post.category}
                  </span>
                  {post.category === "Event" && post.startAt && (
                    <span style={{ fontSize: 12, color: navy, fontFamily: "'Bebas Neue', sans-serif",
                      letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
                      {fmtDate(post.startAt)}
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19,
                  letterSpacing: "0.07em", color: navy, lineHeight: 1.2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {post.title}
                </div>
                <div style={{ fontSize: 11, color: muted, fontFamily: "'Bebas Neue', sans-serif", marginTop: 3, letterSpacing: "0.04em" }}>
                  {fmt(post.createdAt)} &nbsp;·&nbsp; {replyCount} comment{replyCount !== 1 ? "s" : ""}
                </div>
              </div>
              {/* Right: admin actions + chevron */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                {isAdmin && (
                  <button onClick={e => { e.stopPropagation(); onEditPost(post); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: muted, padding: "3px 6px", lineHeight: 1 }}>&#9998;</button>
                )}
                <div style={{ fontSize: 22, color: isOpen ? red : muted, flexShrink: 0,
                  transition: "transform 0.2s, color 0.15s", fontWeight: "bold",
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", paddingLeft: 2 }}>&#9654;</div>
              </div>
            </div>
            {/* ── Expanded content ── */}
            {isOpen && (
              <div style={{ borderTop: `1px solid ${border}` }}>
                {/* Body */}
                <div style={{ padding: "16px 18px", background: "#ffffff" }}>
                  {post.category === "Event" && <EventBadge startAt={post.startAt} endAt={post.endAt} />}
                  {post.category === "Event" && post.location && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(post.location)}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 10,
                        fontSize: 12, color: navy, textDecoration: "none", fontFamily: "'DM Mono', monospace",
                        background: `${navy}0a`, borderRadius: 4, padding: "4px 8px",
                        border: `1px solid ${navy}18` }}>
                      <svg width="11" height="13" viewBox="0 0 12 14" fill="none">
                        <path d="M6 1C3.79 1 2 2.79 2 5c0 3.5 4 8 4 8s4-4.5 4-8c0-2.21-1.79-4-4-4z"
                          fill={red} stroke={red} strokeWidth="0.5"/>
                        <circle cx="6" cy="5" r="1.5" fill="white"/>
                      </svg>
                      {post.location}
                    </a>
                  )}
                  {post.imageUrl && (
                    <div style={{ marginBottom: 12, borderRadius: 6, overflow: "hidden",
                      border: `1px solid ${border}`, maxHeight: 320 }}>
                      <img src={post.imageUrl} alt="" loading="lazy" style={{ width: "100%", maxHeight: 320,
                        objectFit: "cover", display: "block" }} />
                    </div>
                  )}
                  <div style={{ fontSize: 15, color: navy, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                    {linkify(post.body)}
                  </div>
                  {post.link && (
                    <div style={{ marginTop: 12 }}>
                      <a href={post.link} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: red, fontFamily: "'DM Mono', monospace",
                          letterSpacing: "0.08em", textDecoration: "none",
                          border: `1px solid ${red}50`, borderRadius: 4, padding: "5px 12px",
                          display: "inline-block" }}>
                        &#128279; {post.linkLabel || "Open Link"}
                      </a>
                    </div>
                  )}
                </div>
                {/* Comments */}
                <div style={{ background: `${navy}07` }}>
                  {(post.replies || []).map(r => (
                    <div key={r.id} style={{ padding: "10px 16px", borderTop: `1px solid ${border}40`,
                      display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, color: navy, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                          {linkify(r.text)}
                        </div>
                        <div style={{ fontSize: 11, color: muted, fontFamily: "'Bebas Neue', sans-serif", marginTop: 3, letterSpacing: "0.04em" }}>
                          {r.name && <span style={{ color: tan, marginRight: 6 }}>{r.name}</span>}{fmt(r.createdAt)}
                        </div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => onDeleteReply(post.id, r.id)}
                          style={{ background: "none", border: "none", cursor: "pointer",
                            fontSize: 12, color: muted, padding: "2px 4px", flexShrink: 0 }}>&#10005;</button>
                      )}
                    </div>
                  ))}
                  <div style={{ padding: "10px 16px" }}>
                    <button onClick={() => onReply(post.id)}
                      style={{ fontSize: 11, color: red, background: "none", border: `1px solid ${red}50`,
                        borderRadius: 4, padding: "5px 14px", cursor: "pointer",
                        fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
                      + Comment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

export function RichTextArea({ value, onChange, inp2, onImageInsert }) {
  const ref = useRef();
  const imgCount = (value.match(/^!\[\]\(https/gm) || []).length;

  function insertAt(before, after = "") {
    const el = ref.current;
    const start = el.selectionStart, end = el.selectionEnd;
    const selected = value.slice(start, end);
    const newVal = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newVal);
    // Restore cursor after React re-render
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + selected.length + after.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function insertBold() {
    const el = ref.current;
    const start = el.selectionStart, end = el.selectionEnd;
    const selected = value.slice(start, end);
    if (selected) {
      insertAt("**", "**");
    } else {
      insertAt("**bold**");
    }
  }

  function insertBullet() {
    const el = ref.current;
    const start = el.selectionStart;
    // Find start of current line
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const linePrefix = value.slice(lineStart, start);
    if (linePrefix.startsWith("• ")) return; // already bullet
    const newVal = value.slice(0, lineStart) + "• " + value.slice(lineStart);
    onChange(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + 2, start + 2);
    });
  }

  // Auto-continue bullets on Enter
  function handleKeyDown(e) {
    if (e.key === "Enter") {
      const el = ref.current;
      const start = el.selectionStart;
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const linePrefix = value.slice(lineStart, start);
      if (linePrefix.startsWith("• ")) {
        e.preventDefault();
        const newVal = value.slice(0, start) + "\n• " + value.slice(start);
        onChange(newVal);
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(start + 3, start + 3);
        });
      }
    }
  }

  const btnStyle = {
    background: surface, border: `1px solid ${border}`, borderRadius: 3,
    cursor: "pointer", padding: "3px 8px", fontSize: 11, color: navy,
    fontFamily: "'DM Mono', monospace", display: "inline-flex", alignItems: "center",
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 5, alignItems: "center" }}>
        <button type="button" style={btnStyle} onClick={insertBold}
          title="Bold (select text then click, or click to insert)">
          <strong>B</strong>
        </button>
        <button type="button" style={btnStyle} onClick={insertBullet}
          title="Add bullet point">
          • List
        </button>
        {onImageInsert && (
          <button type="button" style={{ ...btnStyle, opacity: imgCount >= 5 ? 0.4 : 1 }}
            disabled={imgCount >= 5}
            onClick={() => onImageInsert(ref.current?.selectionStart ?? value.length)}
            title={imgCount >= 5 ? "Max 5 images" : "Insert photo"}>
            📷 {imgCount > 0 ? `${imgCount}/5` : "Photo"}
          </button>
        )}
      </div>
      <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Write your post..." rows={6} className="rich-body"
        style={{ ...inp2, resize: "vertical", minHeight: 120,
          scrollbarWidth: "auto", scrollbarColor: `${navy}55 ${surface}` }} />
    </div>
  );
}

export function BoardPostModal({ mode, post, parentId, posts, onSavePost, onSaveReply, onCancel, onDeletePost }) {
  const [category, setCategory] = useState(post?.category || "Announcement");
  const [title, setTitle] = useState(post?.title || "");
  const [body, setBody] = useState(post?.body || "");
  const [link, setLink] = useState(post?.link || "");
  const [linkLabel, setLinkLabel] = useState(post?.linkLabel || "");
  const [startAt, setStartAt] = useState(post?.startAt ? new Date(post.startAt).toISOString().slice(0,16) : "");
  const [endAt, setEndAt] = useState(post?.endAt ? new Date(post.endAt).toISOString().slice(0,16) : "");
  const [location, setLocation] = useState(post?.location || "");
  const [replyText, setReplyText] = useState("");
  const [replyName, setReplyName] = useState("");
  const [imageUrl, setImageUrl] = useState(post?.imageUrl || null);
  const [imagePath, setImagePath] = useState(post?.imagePath || null);
  const [imageUploading, setImageUploading] = useState(false);
  const [inlineImageUploading, setInlineImageUploading] = useState(false);
  const [pendingInsertPos, setPendingInsertPos] = useState(null);
  const [gaugeProgress, setGaugeProgress] = useState(null); // { animPct, done }
  const gaugeTargetRef = React.useRef(0);
  const gaugeFrameRef = React.useRef(null);
  const imgRef = useRef();
  const inlineImgRef = useRef();
  const parentPost = mode === "reply" ? posts.find(p => p.id === parentId) : null;

  const inp2 = { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13,
    border: `1px solid ${border}`, borderRadius: 4, background: "#fff",
    fontFamily: "'DM Mono', monospace", outline: "none", color: navy };

  async function handleImageChange(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setImageUploading(true);
    try {
      const postId = post?.id || Date.now().toString();
      const storagePath = `posts/${postId}/image.webp`;
      // Delete old image if replacing
      if (imagePath) { try { await deleteFromStorage(imagePath); } catch(_) {} }
      const blob = await resizeToBlob(f, 1200, 0.82);
      const url = await uploadToStorage(blob, storagePath);
      setImageUrl(url); setImagePath(storagePath);
    } catch (err) { alert("Image upload failed: " + err.message); }
    setImageUploading(false);
  }

  async function removeImage() {
    if (imagePath) { try { await deleteFromStorage(imagePath); } catch(_) {} }
    setImageUrl(null); setImagePath(null);
  }

  function startGauge() {
    gaugeTargetRef.current = 0;
    setGaugeProgress({ animPct: 0, done: false });
    function tick() {
      setGaugeProgress(prev => {
        if (!prev || prev.done) return prev;
        const target = gaugeTargetRef.current;
        const diff = target - prev.animPct;
        if (diff < 0.1) return prev;
        const newPct = prev.animPct + diff * 0.03;
        const done = newPct >= 99.9;
        return { animPct: done ? 100 : newPct, done };
      });
      gaugeFrameRef.current = requestAnimationFrame(tick);
    }
    gaugeFrameRef.current = requestAnimationFrame(tick);
  }

  function stopGauge() {
    if (gaugeFrameRef.current) { cancelAnimationFrame(gaugeFrameRef.current); gaugeFrameRef.current = null; }
  }

  async function handleInlineImageChange(e) {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = "";
    setInlineImageUploading(true);
    startGauge();
    try {
      const postId = post?.id || Date.now().toString();
      const imgId = Date.now().toString();
      const storagePath = `posts/${postId}/inline_${imgId}.webp`;
      const blob = await resizeToBlob(f, 1200, 0.82);
      // Simulate progress via blob size
      gaugeTargetRef.current = 50;
      const { ref: r2, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const { fbStorage } = await import("./firebase");
      const snap = await uploadBytes(r2(fbStorage, storagePath), blob);
      gaugeTargetRef.current = 100;
      const url = await getDownloadURL(snap.ref);
      // Wait for gauge to reach done
      await new Promise(res => {
        const check = setInterval(() => {
          setGaugeProgress(prev => {
            if (prev?.done) { clearInterval(check); res(); }
            return prev;
          });
        }, 50);
      });
      const insertPos = pendingInsertPos ?? body.length;
      const prefix = insertPos > 0 && body[insertPos-1] !== "\n" ? "\n" : "";
      const suffix = insertPos < body.length && body[insertPos] !== "\n" ? "\n" : "";
      const imgMd = `${prefix}![](${url})${suffix}`;
      setBody(prev => prev.slice(0, insertPos) + imgMd + prev.slice(insertPos));
    } catch (err) { alert("Image upload failed: " + err.message); }
    stopGauge();
    setGaugeProgress(null);
    setInlineImageUploading(false);
    setPendingInsertPos(null);
  }

  function handleSubmit() {
    if (mode === "reply") {
      if (!replyText.trim() || !replyName.trim()) return;
      onSaveReply(parentId, replyText.trim(), replyName.trim());
    } else {
      if (!title.trim() || !body.trim()) return;
      onSavePost({
        ...(post || {}), category, title: title.trim(), body: body.trim(),
        link: link.trim() || null, linkLabel: linkLabel.trim() || null,
        imageUrl: imageUrl || null, imagePath: imagePath || null,
        startAt: category === "Event" && startAt ? new Date(startAt).getTime() : null,
        endAt: category === "Event" && endAt ? new Date(endAt).getTime() : null,
        location: category === "Event" ? (location.trim() || null) : null,
      });
    }
  }

  const fieldLabel = (txt) => (
    <div style={{ fontSize: 10, color: muted, letterSpacing: "0.12em", textTransform: "uppercase",
      marginBottom: 5, fontWeight: 600 }}>{txt}</div>
  );

  return (
    <div onClick={e => e.stopPropagation()} style={{ position: "fixed", inset: 0, background: "rgba(10,16,30,0.88)", zIndex: 400,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "20px 16px", overflowY: "auto" }}>
      <div style={{ background: surface, borderRadius: 8, width: "100%", maxWidth: 480,
        boxShadow: "0 8px 40px rgba(0,0,0,0.4)", overflow: "hidden" }}>
        <div style={{ background: navy, padding: "14px 18px", display: "flex", alignItems: "center" }}>
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: "#f2ede4", letterSpacing: "0.1em" }}>
            {mode === "reply" ? "Add Comment" : mode === "edit" ? "Edit Post" : "New Post"}

          </span>
          <button onClick={onCancel} style={{ marginLeft: "auto", background: "none", border: "none",
            color: "#a8b4cc", fontSize: 20, cursor: "pointer", padding: 4 }}>&#10005;</button>
        </div>
        <div style={{ padding: "20px 18px", display: "flex", flexDirection: "column", gap: 14, maxHeight: "80vh", overflowY: "auto" }}>
          {mode === "reply" ? (
            <>
              {parentPost && (
                <div style={{ fontSize: 11, color: muted, fontFamily: "'DM Mono', monospace",
                  padding: "8px 10px", background: `${navy}10`, borderRadius: 4, borderLeft: `3px solid ${red}` }}>
                  Re: {parentPost.title}
                </div>
              )}
              <input value={replyName} onChange={e => setReplyName(e.target.value)}
                placeholder="Your name *" maxLength={40}
                style={{ ...inp2, marginBottom: 8 }} />
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                placeholder="Write a comment... *" rows={4}
                style={{ ...inp2, resize: "vertical" }} />
              {(!replyName.trim() || !replyText.trim()) && (
                <div style={{ fontSize: 10, color: muted, fontFamily: "'DM Mono', monospace" }}>* Both fields required</div>
              )}
            </>
          ) : (
            <>
              {/* Category */}
              <div>
                {fieldLabel("Category")}
                <div style={{ display: "flex", gap: 8 }}>
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setCategory(c)}
                      style={{ padding: "5px 16px", borderRadius: 4, fontSize: 11, cursor: "pointer",
                        fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em",
                        border: `1px solid ${category === c ? red : border}`,
                        background: category === c ? navy : surface,
                        color: category === c ? "#f2ede4" : muted }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              {/* Title — always first after category */}
              <div>
                {fieldLabel("Title")}
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Post title..."
                  style={{ ...inp2 }} />
              </div>
              {/* Event date/time + location */}
              {category === "Event" && (
                <div style={{ background: `${navy}08`, borderRadius: 6, padding: "12px 14px",
                  border: `1px solid ${navy}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 10, color: navy, letterSpacing: "0.12em", textTransform: "uppercase",
                    fontWeight: 700, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>Event Date &amp; Time</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      {fieldLabel("Start")}
                      <input type="datetime-local" lang="en" value={startAt} onChange={e => {
                        const val = e.target.value;
                        setStartAt(val);
                        if (val) {
                          const d = new Date(val);
                          d.setHours(d.getHours() + 1);
                          const pad = n => String(n).padStart(2, "0");
                          const auto = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                          setEndAt(auto);
                        }
                      }} style={{ ...inp2 }} />
                    </div>
                    <div>
                      {fieldLabel("End")}
                      <input type="datetime-local" lang="en" value={endAt} onChange={e => setEndAt(e.target.value)}
                        style={{ ...inp2 }} />
                    </div>
                  </div>
                  <div>
                    {fieldLabel("Location")}
                    <input value={location} onChange={e => setLocation(e.target.value)}
                      placeholder="e.g. Yoyogi Park, Tokyo"
                      style={{ ...inp2 }} />
                    {location.trim() && (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.trim())}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: navy, display: "inline-flex", alignItems: "center",
                          gap: 4, marginTop: 5, textDecoration: "none", opacity: 0.7 }}>
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                          <path d="M6 1C4.07 1 2.5 2.57 2.5 4.5c0 2.8 3.5 6.5 3.5 6.5s3.5-3.7 3.5-6.5C9.5 2.57 7.93 1 6 1z"
                            stroke={navy} strokeWidth="1.2" fill="none"/>
                          <circle cx="6" cy="4.5" r="1.2" fill={navy}/>
                        </svg>
                        Preview on Google Maps
                      </a>
                    )}
                  </div>
                </div>
              )}
              {/* Body with Bold / Bullet toolbar */}
              <div>
                {fieldLabel("Body")}
                <RichTextArea value={body} onChange={setBody} inp2={inp2}
                  onImageInsert={(pos) => {
                    setPendingInsertPos(pos);
                    inlineImgRef.current?.click();
                  }} />
                {gaugeProgress && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                    <FuelGauge animPct={gaugeProgress.animPct} done={gaugeProgress.done} />
                  </div>
                )}
              </div>
              {/* inline images handled via 📷 button in body toolbar */}
              <input ref={inlineImgRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleInlineImageChange} />
              {/* Link */}
              <div style={{ background: `${navy}06`, borderRadius: 6, padding: "12px 14px",
                border: `1px solid ${border}`, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 10, color: muted, letterSpacing: "0.12em", textTransform: "uppercase",
                  fontWeight: 600 }}>&#128279; Link (optional)</div>
                <input value={link} onChange={e => setLink(e.target.value)}
                  placeholder="https://..." style={{ ...inp2 }} />
                <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)}
                  placeholder="Button label (e.g. Register Here)" style={{ ...inp2 }} />
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4, alignItems: "center" }}>
            {mode === "edit" && onDeletePost && (
              <button onClick={() => { if (window.confirm("Delete this post?")) { onDeletePost(post.id); onCancel(); } }}
                style={{ marginRight: "auto", padding: "8px 18px", borderRadius: 4,
                  border: `1px solid ${red}50`, background: "none", color: red, fontSize: 12,
                  cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
                Delete
              </button>
            )}
            <button onClick={onCancel}
              style={{ padding: "8px 18px", borderRadius: 4, border: `1px solid ${border}`,
                background: "none", color: muted, fontSize: 12, cursor: "pointer",
                fontFamily: "'DM Mono', monospace" }}>Cancel</button>
            <Btn onClick={handleSubmit}>
              {mode === "reply" ? "Post Comment" : mode === "edit" ? "Save" : "Publish"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}



